import type { Tariff, TariffMarking } from "types";
import { isTariffManagerOrigin } from "@/utils/tariffManagerSearch";

export const TARIFF_NOTE_MAX_LENGTH = 500;

export function normalizeTariffMarkings(raw: unknown): TariffMarking[] {
  if (!Array.isArray(raw)) return [];
  const byId = new Map<string, TariffMarking>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const tariffId = String(row.tariff_id ?? "").trim();
    if (!tariffId) continue;
    const note = String(row.note ?? "").slice(0, TARIFF_NOTE_MAX_LENGTH);
    const bookmarked = Boolean(row.bookmarked);
    if (!bookmarked) continue;
    const marking: TariffMarking = {
      tariff_id: tariffId,
      bookmarked,
      note,
    };
    if (typeof row.tariff_name === "string" && row.tariff_name) {
      marking.tariff_name = row.tariff_name;
    }
    if (typeof row.price === "number" && Number.isFinite(row.price)) {
      marking.price = row.price;
    }
    if (typeof row.payment_interval === "string" && row.payment_interval) {
      marking.payment_interval = row.payment_interval;
    }
    if (typeof row.insurer_slug === "string" && row.insurer_slug) {
      marking.insurer_slug = row.insurer_slug;
    }
    byId.set(tariffId, marking);
  }
  return Array.from(byId.values());
}

export function markingFromTariff(
  tariff: Tariff,
  overrides: Partial<Pick<TariffMarking, "bookmarked" | "note">> & {
    payment_interval?: string;
  } = {},
): TariffMarking {
  const tariffId = String(tariff.legacy_id ?? tariff.id ?? "");
  const note = String(overrides.note ?? "").slice(0, TARIFF_NOTE_MAX_LENGTH);
  return {
    tariff_id: tariffId,
    tariff_name: tariff.name_de,
    price: tariff.price,
    payment_interval:
      overrides.payment_interval ||
      String(tariff.payment_interval || "").trim() ||
      undefined,
    insurer_slug: tariff.insurer?.slug,
    bookmarked: Boolean(overrides.bookmarked),
    note,
  };
}

export function shouldKeepMarking(
  marking: Pick<TariffMarking, "bookmarked" | "note">,
): boolean {
  return Boolean(marking.bookmarked);
}

/** Merge patch into list; drop entry when neither bookmarked nor note. */
export function upsertTariffMarkingInList(
  list: TariffMarking[],
  next: TariffMarking,
): TariffMarking[] {
  const without = list.filter((m) => m.tariff_id !== next.tariff_id);
  if (!shouldKeepMarking(next)) {
    return without;
  }
  return [...without, next];
}

export function findTariffMarking(
  list: TariffMarking[],
  tariffId: string,
): TariffMarking | undefined {
  const id = String(tariffId ?? "").trim();
  if (!id) return undefined;
  return list.find((m) => m.tariff_id === id);
}

export function canEditTariffMarkings(
  activeUser: string | null | undefined,
): boolean {
  const role = String(activeUser || "")
    .trim()
    .toLowerCase();
  return !role || role === "owner" || role === "broker";
}

export function shouldShowTariffMarkings(options: {
  completedBy: string | null | undefined;
  notAtBooking: boolean;
  isTariffPage: boolean;
  /** Prod-safe gate: pin/comment UI only when opened from tariff-manager. */
  origin?: string | null;
}): boolean {
  return (
    isTariffManagerOrigin(options.origin) &&
    options.notAtBooking &&
    !options.isTariffPage &&
    options.completedBy === "broker"
  );
}
